import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { QUESTIONNAIRE } from "@/lib/questionnaire";
import { useLookups } from "@/lib/useLookups";
import { QuestionnaireForm } from "@/components/QuestionnaireForm";
import { PageHeader, Panel, Field, GoldButton, GhostButton, Select, inputCls } from "@/components/common";

export const emptyClient = { first_name: "", last_name: "", phone: "", email: "", birth_date: "", address: "", notes: "", operator_id: "", questionnaire: {} };

export const ClientInfoForm = ({ f, setF, operators }) => {
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="grid gap-5 sm:grid-cols-2">
      <Field label="Nome *"><input className={inputCls} value={f.first_name} onChange={set("first_name")} data-testid="client-first-name-input" /></Field>
      <Field label="Cognome *"><input className={inputCls} value={f.last_name} onChange={set("last_name")} data-testid="client-last-name-input" /></Field>
      <Field label="Telefono"><input className={inputCls} value={f.phone} onChange={set("phone")} data-testid="client-phone-input" /></Field>
      <Field label="Email"><input type="email" className={inputCls} value={f.email} onChange={set("email")} data-testid="client-email-input" /></Field>
      <Field label="Data di nascita"><input type="date" className={inputCls} value={f.birth_date} onChange={set("birth_date")} data-testid="client-birth-input" /></Field>
      <Field label="Operatore di riferimento">
        <Select value={f.operator_id} onChange={set("operator_id")} placeholder="Nessuno" options={operators.map((o) => ({ value: o.id, label: o.name }))} data-testid="client-operator-select" />
      </Field>
      <div className="sm:col-span-2"><Field label="Indirizzo"><input className={inputCls} value={f.address} onChange={set("address")} data-testid="client-address-input" /></Field></div>
      <div className="sm:col-span-2"><Field label="Note"><textarea rows={3} className={inputCls} value={f.notes} onChange={set("notes")} data-testid="client-notes-input" /></Field></div>
    </div>
  );
};

const STEPS = ["Anagrafica", ...QUESTIONNAIRE.map((s) => s.title)];

export default function NewClient() {
  const [step, setStep] = useState(0);
  const [f, setF] = useState(emptyClient);
  const [saving, setSaving] = useState(false);
  const { operators } = useLookups(["operators"]);
  const nav = useNavigate();

  const next = () => {
    if (step === 0 && (!f.first_name.trim() || !f.last_name.trim())) return toast.error("Inserisci nome e cognome");
    setStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/clients", f);
      toast.success("Cliente inserito");
      nav(`/clienti/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); setSaving(false); }
  };
  const last = step === STEPS.length - 1;

  return (
    <div data-testid="new-client-page">
      <PageHeader overline="Nuovo cliente" title={step === 0 ? "Dati anagrafici" : "Scheda esigenze"} />
      <ol className="mb-8 flex flex-wrap gap-2">
        {STEPS.map((s, i) => (
          <li key={s} data-testid={`wizard-step-${i}`} className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${i === step ? "border-[#C5A059] bg-[#121212] text-[#F6F1EA]" : i < step ? "border-[#C5A059]/50 text-[#8A6A22] bg-[#C5A059]/10" : "border-[#E8E1D7] text-[#9E958A]"}`}>
            {i < step ? <Check className="h-3 w-3" /> : <span>{i + 1}</span>}{s}
          </li>
        ))}
      </ol>
      <Panel className="p-6 sm:p-10 fade-up" key={step}>
        {step === 0
          ? <ClientInfoForm f={f} setF={setF} operators={operators} />
          : <QuestionnaireForm sections={[QUESTIONNAIRE[step - 1]]} value={f.questionnaire} onChange={(q) => setF({ ...f, questionnaire: q })} />}
      </Panel>
      <div className="mt-6 flex justify-between">
        <GhostButton onClick={() => (step ? setStep(step - 1) : nav("/clienti"))} data-testid="wizard-back-button"><ArrowLeft className="h-4 w-4" /> {step ? "Indietro" : "Annulla"}</GhostButton>
        {last
          ? <GoldButton onClick={save} disabled={saving} data-testid="wizard-save-button"><Check className="h-4 w-4" /> Salva cliente</GoldButton>
          : <GoldButton onClick={next} data-testid="wizard-next-button">Avanti <ArrowRight className="h-4 w-4" /></GoldButton>}
      </div>
    </div>
  );
}
